import { Option } from '../models/Option'
import { Address } from './fields'
import { documentTypeInfo } from '../pages/applicationForm-2/documents/DocumentTypeInfo'
import { ApplicationModel } from '../models/ApplicationModel'
import { ApplicationExportMarketFinancialsModel } from '../models/ApplicationExportMarketFinancialsModel'

export function formatDigits(v: string) {
  if (!v) {
    return v
  }

  let res = ''

  v = v.replace(/[^\d]/g, '')
  let i = 0
  for (; i < Math.floor(v.length / 3); i++) {
    res = v.substr(v.length - 3 * (i + 1), 3) + (i ? ' ' : '') + res
  }
  const remaining = v.substr(0, v.length - i * 3)
  res = remaining + (remaining ? ' ' : '') + res

  return res
}

function isInteger(v: number): boolean {
  return v % 1 === 0
}

function getDecimalString(v: number): string {
  return (`${v}`).split('.')[1]
}

export function customRound(v: number): number {
  // return unchanged if whole number
  if (isInteger(v)) return v

  // If need even mid point rounding
  // if (/5$/.test(getDecimalString(v)))
  //    return Math.round(v / 2) * 2;   //Round to the nearest even (to match .net rounding)

  return Math.round(v)
}

export function getPercentage(amount: number, total: number): number {
  if (amount && total) {
    return Math.round((amount / total) * 10000) / 10000
  }

  return null
}

export function newGuid() {
  let d = new Date().getTime()
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    d += performance.now() // use high-precision timer if available
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (d + Math.random() * 16) % 16 | 0
    d = Math.floor(d / 16)
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

export function getAddressString(a: Address) {
  if (!a) return ''
  const line1 = a.line1 ? `${a.line1}, ` : ''
  const line2 = a.line2 ? `${a.line2}, ` : ''
  const suburb = a.suburb ? `${a.suburb}, ` : ''
  const postCode = a.postalCode ? `${a.postalCode}, ` : ''
  const state = a.state ? `${a.state}, ` : ''
  const country = a.country ? `${a.country}` : ''

  return line1 + line2 + suburb + state + postCode + country
}

export function formatNumber(v: number, defaultValue: string = '') {
  let res = defaultValue

  if (v != null) {
    // Round to 2 decimals
    v = Math.round(v * 100) / 100

    if (isInteger(v)) res = v.toFixed(2).toString()
    else res = v.toFixed(2)

    res = res.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  return res
}

export function formatDateForDisplay(v: string, defaultValue: string = '') {
  if (!v) return defaultValue

  const parts = v.split('-')

  if (parts.length != 3) return defaultValue

  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

export function formatMonthForDisplay(v: string, defaultValue: string = '') {
  if (!v) return defaultValue

  const parts = v.split('-')

  if (parts.length != 3) return defaultValue

  return `${parts[1]}/${parts[0]}`
}

export function formatPaymentMethodForDisplay(v: number, defaultValue: string = '') {
  if (v == null) return defaultValue

  const pm = paymentMethodDataSource.filter((p) => p.value == v)
  if (pm && pm.length > 0) return pm[0].label
}

export function formatPercentageForDisplay(v: number, defaultValue: string = '') {
  if (!v) return defaultValue

  return `${formatNumber(v * 100)}%`
}

export function formatMoneyForDisplay(v: number, defaultValue: string = '') {
  if (!v) return defaultValue

  return `$${formatNumber(v)}`
}

export function getDateAsString(v: Date) {
  if (v == null) {
    return null
  }

  return `${v.getFullYear()}-${(`0${v.getMonth() + 1}`).slice(-2)}-${(`0${v.getDate()}`).slice(-2)}`
}

export function getDateFromString(v: string, includeTime: boolean = false): Date {
  if (!v) {
    return null
  }

  const date = new Date(v)

  if (!includeTime) date.setHours(0, 0, 0, 0)

  return date
}

export function getFY(year: number): string {
  return `${year - 1}-${year.toString().length == 4 ? year.toString().substr(2) : year}`
}

function getCurrentFYYear(): number {
  const date = new Date()
  const month = date.getMonth()
  const fullYear = date.getFullYear()

  return month > 5 ? fullYear + 1 : fullYear
}

export function getFYForDisplay(activeYear?: number): string {
  let year = activeYear
  if (!year) {
    year = getCurrentFYYear()
  }
  return `${year - 1}-${year.toString().substr(2)}`
}

export function getLastFYForDisplay(): string {
  const lastFYYear = getCurrentFYYear() - 1
  return getFYForDisplay(lastFYYear)
}

export function get2ndLastFYForDisplay(): string {
  const lastFYYear = getCurrentFYYear() - 2
  return getFYForDisplay(lastFYYear)
}

export function get3rdLastFYForDisplay(): string {
  const lastFYYear = getCurrentFYYear() - 3
  return getFYForDisplay(lastFYYear)
}

// returns string of the form: '2021-22'; Get GUID in CRM for 2021;
export function getLastFY(date: Date): string {
  if (date instanceof Date) {
    const month = date.getMonth()
    const year = date.getFullYear()
    return month > 5 ? `${year - 1}-${year.toString().substring(2)}` : `${year - 2}-${(year - 1).toString().substring(2)}`
  }

  return ''
}
export function getThisFY(date: Date): string {
  if (date instanceof Date) {
    const month = date.getMonth()
    const year = date.getFullYear()
    return month > 5 ? `${year}-${(year + 1).toString().substring(2)}` : `${year - 1}-${(year).toString().substring(2)}`
  }

  return ''
}
export function getNextFY(date: Date): string {
  if (date instanceof Date) {
    const month = date.getMonth()
    const year = date.getFullYear()
    return month > 5 ? `${year + 1}-${(year + 2).toString().substring(2)}` : `${year - 2}-${(year - 1).toString().substring(2)}`
  }

  return ''
}

export function calculateDaysInclusive(startDate: Date, endDate: Date) {
  if (!startDate || !endDate) return null

  const oneDayMs = 1000 * 60 * 60 * 24

  const daysInBetween = Math.round((endDate.setHours(0, 0, 0, 0) - startDate.setHours(0, 0, 0, 0)) / oneDayMs)

  if (daysInBetween < 0) return 0

  return daysInBetween + 1
}

export function grantPeriodStart(grantYear: number, claimTwoYrs: boolean, adjustYearAdd: number = 0) {
  if (grantYear) return new Date(grantYear - (claimTwoYrs ? 2 : 1) + (adjustYearAdd || 0), 6, 1)

  return null
}

export function grantPeriodEnd(grantYear: number, adjustYearAdd: number = 0) {
  if (grantYear) return new Date(grantYear + (adjustYearAdd || 0), 5, 30)

  return null
}

export function getMostRecentExportStart(adjustMonths:number): Date {
  const date = new Date()
  const newDate = new Date(date.getFullYear(), date.getMonth() - adjustMonths, 1)
  return newDate
}

export function getMarketString(selectedMarkets: string[], allMarkets: Option<string>[]) {
  let res = ''
  if (selectedMarkets) {
    for (const m of selectedMarkets) {
	  const match = allMarkets.filter((r) => r.value == m)
	  if (match.length == 0) {
        continue
	  }
	  if (res.length > 0) {
        res += ', '
	  }
	  res += match[0].label
    }
  }
  return res
}

export function getMarketCountries(markets: Option<string>[], marketsRegions: string[]) {
  // remove 'worldwide and market regions from the list of markets
  const filteredMarkets = markets.filter((item: any) => item.label !== 'Worldwide').filter((m) => !marketsRegions.some((mr) => mr === m.value))
  return filteredMarkets
}

export function fillExportMarketFinancialsWithDefaults(exportMarketFinancials: ApplicationExportMarketFinancialsModel[]): ApplicationExportMarketFinancialsModel[] {
  // insert missing fields to their default values
  const finArray = [...exportMarketFinancials]
  const fullRowsArr = finArray.map((row) => {
    if (!row.crmExportMarketID) {
      row.crmExportMarketID = '00000000-0000-0000-0000-000000000000'
    }
    if (!row.earnings) {
      row.earnings = 0
    } else if (isNaN(row.earnings)) { // it is defined, but still NaN
      row.earnings = 0
    }
    return row
  })
  return fullRowsArr
}

export function readCookieValue(cookieName: string): string {
  const match = document.cookie.match(`(^|[^;]+)\\s*${cookieName}\\s*=\\s*([^;]+)`)
  return match ? unescape(match.pop()) : null
}

export function sortSchedules(aInvalid: boolean, bInvalid: boolean, aReference: string, bReference: string): number {
  if (aInvalid && !bInvalid) return -1

  if (bInvalid && !aInvalid) return 1

  const maybeNumA = Number(aReference)
  const maybeNumB = Number(bReference)

  if (isNaN(maybeNumA)) {
    if (isNaN(maybeNumB)) {
	  if (aReference < bReference) return -1
	  return 1
    }

    return 1
  }

  if (isNaN(maybeNumB)) {
    return -1
  }

  return maybeNumA - maybeNumB
}

export function getInvalidFileBlobIds(model: ApplicationModel) {
  const currentCodes: string[] = []
  const invalidCodes: string[] = []
  documentTypeInfo.map((r) => (
    r.reducer(model).map((i) => (
      currentCodes.push(r.code)
    ))))
  model.applicationDocuments && model.applicationDocuments.forEach((uploadedDocuments) => {
    if (currentCodes.indexOf(uploadedDocuments.code) === -1) {
      invalidCodes.push(uploadedDocuments.blobID)
    }
  })
  return invalidCodes
}

export function isEmpty(str:string) {
  return !str || !str.trim()
}

function cleanseAssertionOperators(parsedName) {
  return parsedName.replace(/[?!]/g, '')
}
export function nameof(nameFunction) {
  const fnStr = nameFunction.toString()
  // ES6 class name:
  // "class ClassName { ..."
  if (fnStr.startsWith('class ')
        // Theoretically could, for some ill-advised reason, be "class => class.prop".
        && !fnStr.startsWith('class =>')) {
    return cleanseAssertionOperators(fnStr.substring('class '.length, fnStr.indexOf(' {')))
  }
  // ES6 prop selector:
  // "x => x.prop"
  if (fnStr.includes('=>')) {
    return cleanseAssertionOperators(fnStr.substring(fnStr.indexOf('.') + 1))
  }
  // ES5 prop selector:
  // "function (x) { return x.prop; }"
  // webpack production build excludes the spaces and optional trailing semicolon:
  //   "function(x){return x.prop}"
  // FYI - during local dev testing i observed carriage returns after the curly brackets as well
  // Note by maintainer: See https://github.com/IRCraziestTaxi/ts-simple-nameof/pull/13#issuecomment-567171802 for explanation of this regex.
  const matchRegex = /function\s*\(\w+\)\s*\{[\r\n\s]*return\s+\w+\.((\w+\.)*(\w+))/i
  const es5Match = fnStr.match(matchRegex)
  if (es5Match) {
    return es5Match[1]
  }
  // ES5 class name:
  // "function ClassName() { ..."
  if (fnStr.startsWith('function ')) {
    return cleanseAssertionOperators(fnStr.substring('function '.length, fnStr.indexOf('(')))
  }
  // Invalid function.
  throw new Error('ts-simple-nameof: Invalid function.')
}

export const paymentMethodDataSource = [
  { value: 0, label: 'BPay' },
  { value: 1, label: 'Cash' },
  { value: 2, label: 'Cheque' },
  { value: 3, label: 'Contra' },
  { value: 4, label: 'Credit Card' },
  { value: 5, label: 'Debit Card' },
  { value: 6, label: 'Direct Debit' },
  { value: 7, label: 'EFT' },
  { value: 8, label: 'Journal' },
  { value: 9, label: 'Offset' },
  { value: 10, label: 'Paypal' },
  { value: 11, label: 'T/T' },
]

export const BusinessCategories = {
  Individual: 1, // IND
  Company: 2, // C
  CoOperative: 3, // COOP
  Partnership: 4, // P
  BodyCorp: 5, // PUB
  ApprovedJointVenture: 6, // V
  ApprovedBody: 7, // B
  Trust: 8, // TRUS
  GovernmentEntity: 9, // GOV
}

export const BusinessCategoriesReform = {
  Individual: 498650000, // IND
  Company: 498650001, // C
  CoOperative: 498650002, // COOP
  Partnership: 498650003, // P
  BodyCorp: 498650004, // PUB
  Trust: 498650005, // TRUS
}

export const CoreProducts = {
  Goods: 1,
  Services: 2,
  Events: 3,
  IntellectualProperty: 4,
  KnowHow: 5,
  Software: 6,
}

export const PerformanceMeasures = {
  OptionA: 1, // EPT
  OptionB: 2, // ANB
}

export const PrincipalAdditionalDocument = {
  InternationalSalesInvoiceOrContractInRelationToGrantYear: 1,
  InternationalSalesInvoiceOrContractPostGrantYear: 2,
  DomesticSalesInvoice: 3,
  BankStatementShowingPayment: 4,
  Contract: 5,
  Other: 6,
}

export const LodgementPeriodType = {
  Lodgement: 0,
  PreLodgement: 1,
}

export const ConsultantType = {
  Individial: 0,
  Consultant: 1,
  QIPConsultant: 2,
}

export const ScheduleTypes = {
  OverseasRepresentative: 970150000,
  MarketingConsultant: 970150002,
  MarketingVisits: 970150003,
  FreeSamples: 970150004,
  TradeFairsPromotionalEvents: 970150005,
  PromotionalLiteratureAdvertising: 970150006,
  OverseasBuyers: 970150007,
  IntellectualPropertyRegistration: 970150008,
  IntellectualPropertyInsurance: 970150013,
  Goods: 970150001,
  ServicesIPRKnowHow: 970150009,
  TourismServices: 970150010,
  EventsPromoters: 970150011,
  RelatedEntities: 970150012,
}

export const RepresentativeCategories = {
  ByLand: 498650000,
  WaterOrAir: 498650001,
  NightAccomodation: 498650002,
  Tours: 498650003,
  PaidAdmission: 498650004,
  Event: 498650005,
  Restaurant: 498650006,
  Gambling: 498650007,
}

export const CompanyRelationship = {
  Parentcompany: 'Parent-company',
  Subsidiarycompany: 'Subsidiary-company',
  Subsidiarywithsameparentcompany: 'Subsidiary-with-same-parent-company',
  Shareholder: 'Shareholder',
}

export const Salutation = {
  Mr: 498650000,
  Mrs: 498650001,
  Ms: 498650002,
  Miss: 498650003,
  Other: 498650004,
}

export const YesNoPrefer = {
  No: 498650000,
  Yes: 498650001,
  PreferNotToAnswer: 498650002,
}

export const AustraliaResearchChoices = {
  Wholly: '498650000',
  Substantially: '498650001',
  None: '498650002',
}

export const TierComplianceRequirements = {
  OthersCrmId: 'Others',
  NotApplicableCrmId: 'NotApplicable',
}

export const ApplicationTiers = {
  Tier1: '498650000',
  Tier2: '498650001',
  Tier3: '498650002',
}

export const StrategicShiftTypes = {
  RBOption1: 498650000,
  RBOption2: 498650001,
  RBOption3: 498650002,
}

export const ApplicationCompletionTimeRanges = {
  LessThan30Mins: '498650000',
  LessThan60Mins: '498650001',
  LessThan90Mins: '498650002',
  LessThan2Hrs: '498650003',
  MoreThan2Hrs: '498650004',
}

export const ApplicationCompletionDifficultyRatings = {
  VeryDifficult: '498650000',
  Difficult: '498650001',
  Neutral: '498650002',
  Easy: '498650003',
  VeryEasy: '498650004',
}

export const ApplicationCompletionImprovementOptions = {
  DecreaseNumberOfQuestions: '498650000',
  SimplifyWording: '498650001',
  ImproveLayout: '498650002',
  ProvideHelp: '498650003',
  Other: '498650004',
}

export const EligibleExpenditureCategory = {
  OverseasRep: 'Maintaining representative in a foreign country',
  ShortTripsForeign: 'Short trips to a foreign country',
  Consultant: 'Consultants',
  ShortTripsAustralia: 'Short trips within Australia',
  ForeignBuyerVisit: 'Foreign buyer visits',
  SolicitingBusinessForeign: 'Soliciting for business in foreign country',
  FreeSample: 'Free samples',
  PromotionAdvertisingMaterial: 'Promotional and advertising material',
  IntellectualPropertyRights: 'Intellectual property rights',
  Training: 'Training',
  ExportTraining: 'Export training activities (applies to Tier 1 and Representative Body)',
  NoExpenditure: 'No expenditure',
}
